import createClient, { type Middleware } from 'openapi-fetch';

import { deviceId } from '@/lib/device';
import { env } from '@/lib/env';

import {
  ApiError,
  MOTIVOS_RESTRICCION,
  SESSION_ENDED,
  cuentaRestringidaDe,
  type CuentaRestringida,
} from './errors';
import type { components, paths } from './schema';

export type Authenticated = components['schemas']['Authenticated'];
export type User = components['schemas']['User'];

/* ----------------------------------------------------------------- estado del token */

/** El access token vive solo en memoria (plan §6.1); el refresh, en cookie HttpOnly. */
let accessToken: string | null = null;
type Listener = (
  event:
    | { type: 'token'; token: string | null }
    | { type: 'ended'; code: string; restriccion?: CuentaRestringida },
) => void;
const listeners = new Set<Listener>();

export function onAuthEvent(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function setAccessToken(token: string | null) {
  accessToken = token;
  listeners.forEach((l) => l({ type: 'token', token }));
}
function sessionEnded(code: string, restriccion?: CuentaRestringida) {
  accessToken = null;
  listeners.forEach((l) =>
    l(restriccion ? { type: 'ended', code, restriccion } : { type: 'ended', code }),
  );
}

/* ---------------------------------------------------------------------- mantenimiento */

/** La API responde `503 MAINTENANCE` durante el corte M2 (schema v2 §9.5). */
type MaintenanceListener = (info: { until?: string }) => void;
const maintenanceListeners = new Set<MaintenanceListener>();

export function onMaintenance(fn: MaintenanceListener): () => void {
  maintenanceListeners.add(fn);
  return () => maintenanceListeners.delete(fn);
}

/* -------------------------------------------------------------------------- envelope */

interface ErrorBody {
  error?: { code?: string; message?: string; details?: Record<string, unknown> };
  meta?: { requestId?: string };
}

async function toApiError(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.clone().json();
  } catch {
    /* respuesta sin JSON (proxy, red) */
  }
  return apiErrorDe(res, body);
}

/** `ApiError` a partir del cuerpo ya leído (openapi-fetch consume el body: no se puede clonar). */
function apiErrorDe(res: Response, cuerpo: unknown): ApiError {
  const body: ErrorBody = cuerpo && typeof cuerpo === 'object' ? cuerpo : {};
  return new ApiError(
    res.status,
    body.error?.code ?? 'HTTP_ERROR',
    body.error?.message ?? 'Ocurrió un error inesperado. Inténtalo de nuevo en unos minutos.',
    body.error?.details ?? {},
    body.meta?.requestId,
  );
}

const baseHeaders = () => ({
  'X-Client': 'web',
  'X-Client-Version': '0.1.0',
  'X-Device-Id': deviceId(),
  'X-Requested-With': 'consalud-web',
});

/* --------------------------------------------------------------------------- refresh */

let refreshing: Promise<Authenticated | null> | null = null;

async function doRefresh(): Promise<Authenticated | null> {
  for (let attempt = 0; attempt < 3; attempt++) {
    const res = await fetch(`${env.apiUrl}/api/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: baseHeaders(),
    });
    if (res.ok) {
      const { data } = (await res.json()) as { data: Authenticated };
      setAccessToken(data.accessToken);
      return data;
    }
    const err = await toApiError(res);
    // Otra pestaña acaba de rotar el refresh: la cookie nueva ya está en el navegador.
    if (err.code === 'REFRESH_CONFLICT') {
      await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
      continue;
    }
    // Suspendida o bloqueada a media sesión: se cierra la local y el login muestra el modal.
    const restriccion = cuentaRestringidaDe(err);
    if (restriccion) {
      sessionEnded(err.code, restriccion);
      return null;
    }
    if (res.status === 401) {
      // Expulsada por otro inicio de sesión: avisar en el login (sesión única, plan §6.6).
      if (err.code === 'SESSION_REVOKED') sessionEnded(err.code);
      return null;
    }
    throw err;
  }
  return null;
}

/**
 * Un solo refresh a la vez en todo el navegador: dentro de la pestaña con una promesa
 * compartida y entre pestañas con Web Locks (si no, dos pestañas usarían el mismo refresh y
 * la API lo trataría como reuso).
 */
export function refreshSession(): Promise<Authenticated | null> {
  refreshing ??= (async () => {
    try {
      if ('locks' in navigator) {
        return await navigator.locks.request('cs-refresh', () => doRefresh());
      }
      return await doRefresh();
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/* ---------------------------------------------------------------------------- client */

const authMiddleware: Middleware = {
  onRequest({ request }) {
    Object.entries(baseHeaders()).forEach(([k, v]) => request.headers.set(k, v));
    if (accessToken && !request.headers.has('Authorization')) {
      request.headers.set('Authorization', `Bearer ${accessToken}`);
    }
    return request;
  },
};

/** fetch con reintento tras renovar el token expirado (una sola vez por request). */
async function authFetch(input: Request): Promise<Response> {
  const retry = input.clone();
  const res = await fetch(input);
  if (res.status === 503) {
    const err = await toApiError(res);
    if (err.code === 'MAINTENANCE') {
      const until = typeof err.details.until === 'string' ? err.details.until : undefined;
      maintenanceListeners.forEach((l) => l(until ? { until } : {}));
    }
    return res;
  }
  if (res.status === 403 && accessToken) {
    const err = await toApiError(res);
    const restriccion = cuentaRestringidaDe(err);
    if (restriccion) sessionEnded(err.code, restriccion);
    return res;
  }
  if (res.status !== 401 || !accessToken) return res;
  const err = await toApiError(res);
  // Expiró el token o un admin suspendió/bloqueó la cuenta con el token vivo (`SESSION_EXPIRED`
  // con `details.reason` admin_*): el refresh trae los datos del modal (403) o renueva la sesión.
  const porRestriccion =
    err.code === 'SESSION_EXPIRED' && MOTIVOS_RESTRICCION.has(err.details.reason);
  if (err.code === 'TOKEN_EXPIRED' || porRestriccion) {
    const refreshed = await refreshSession();
    if (refreshed) {
      retry.headers.set('Authorization', `Bearer ${refreshed.accessToken}`);
      return fetch(retry);
    }
    // Si el refresh ya cerró la sesión con su motivo (SESSION_REVOKED, cuenta suspendida…),
    // no se pisa con SESSION_EXPIRED.
    if (accessToken) sessionEnded('SESSION_EXPIRED');
    return res;
  }
  if (SESSION_ENDED.has(err.code)) sessionEnded(err.code);
  return res;
}

export const api = createClient<paths>({
  baseUrl: env.apiUrl,
  credentials: 'include',
  fetch: authFetch,
});
api.use(authMiddleware);

/** Desenvuelve `{ data }` o lanza `ApiError` (el mensaje viene en español desde la API). */
export async function unwrap<T>(
  promise: Promise<{ data?: { data: T }; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await promise;
  if (!response.ok || !data) throw apiErrorDe(response, error);
  return data.data;
}

/**
 * Envío que sobrevive al cierre de la pestaña (`keepalive`): el autosave del simulador lo usa en
 * `pagehide`. No reintenta ni refresca el token: si falla, el último guardado normal ya está.
 */
export function sendOnUnload(path: string, method: 'PUT' | 'POST', body: unknown): void {
  if (!accessToken) return;
  void fetch(`${env.apiUrl}${path}`, {
    method,
    keepalive: true,
    credentials: 'include',
    headers: {
      ...baseHeaders(),
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  }).catch(() => undefined);
}
